import { ChatSessionApi } from '@myre/app-agent-generated/sdk/angular/ChatSessionApi';
import { type ChatMessage } from '@myre/app-agent-generated/types/ChatMessage';
import { type ChatSession } from '@myre/app-agent-generated/types/ChatSession';

import { inject, Injectable, signal } from '@angular/core';
import { tap, type Observable } from 'rxjs';

import { ChatContextService } from './chat-context.service';

@Injectable({ providedIn: 'root' })
export class ChatSessionService {
  private readonly _chatSessionApi = inject(ChatSessionApi);
  private readonly _chatContextService = inject(ChatContextService);

  private readonly _sessions = signal<readonly ChatSession[]>([]);
  private readonly _activeSessionId = signal<string | null>(null);
  private readonly _loading = signal(false);

  public readonly sessions = this._sessions.asReadonly();
  public readonly activeSessionId = this._activeSessionId.asReadonly();
  public readonly loading = this._loading.asReadonly();

  public loadSessions(): void {
    this._loading.set(true);
    const customerId = this._getCustomerId();

    this._chatSessionApi.getChatSessions({ customerId }).subscribe({
      next: sessions => {
        this._sessions.set(sessions);
        this._loading.set(false);
      },
      error: () => this._loading.set(false),
    });
  }

  public loadMessages(sessionId: string): Observable<readonly ChatMessage[]> {
    const customerId = this._getCustomerId();

    return this._chatSessionApi.listMessages({ customerId, sessionId });
  }

  public getSession(sessionId: string): Observable<ChatSession> {
    const customerId = this._getCustomerId();

    return this._chatSessionApi.getChatSession({ customerId, sessionId });
  }

  /**
   * Record that the user cancelled the session's pending approval card, so a
   * reload renders it cancelled (not a re-armed pending gate). Display-only — the
   * suspended graph is never resumed.
   */
  public cancelApproval(sessionId: string): Observable<unknown> {
    const customerId = this._getCustomerId();

    return this._chatSessionApi.cancelApproval({ customerId, sessionId });
  }

  public selectSession(sessionId: string): void {
    this._activeSessionId.set(sessionId);
  }

  public clearActiveSession(): void {
    this._activeSessionId.set(null);
  }

  /**
   * Renames a session and reflects the new title in the cached list. Returns the
   * updated session so callers can sync any open window tab title.
   */
  public renameSession(
    sessionId: string,
    title: string,
  ): Observable<ChatSession> {
    const customerId = this._getCustomerId();

    return this._chatSessionApi
      .updateChatSession({ customerId, sessionId }, { title })
      .pipe(
        tap(session =>
          this._sessions.update(sessions =>
            sessions.map(s => (s.id === sessionId ? session : s)),
          ),
        ),
      );
  }

  public deleteSession(sessionId: string): void {
    const customerId = this._getCustomerId();

    this._chatSessionApi
      .deleteChatSession({ customerId, sessionId })
      .subscribe({
        next: () => {
          this._sessions.update(sessions =>
            sessions.filter(s => s.id !== sessionId),
          );

          if (this._activeSessionId() === sessionId) {
            this._activeSessionId.set(null);
          }
        },
      });
  }

  private _getCustomerId(): number {
    return this._chatContextService.context().params.customerId;
  }
}
